// src/components/FolderPickerButton.tsx
import React, { useEffect, useCallback, useRef } from "react";
import { msalInstance } from "../msalInstance";

type PickedFolder = {
  id: string;
  driveId: string;
  webUrl: string;
  name: string;
};

type Props = {
  onPicked: (folder: PickedFolder) => void;
  label?: string;
};

const OD_BUSINESS_ENDPOINT_HINT = process.env.REACT_APP_SHAREPOINT_HINT || "https://leoberwick-my.sharepoint.com/";

export default function FolderPickerButton({
  onPicked,
  label = "Select folder",
}: Props) {
  // Load OneDrive SDK once
  useEffect(() => {
    const od = (window as any).OneDrive;
    if (!od) {
      const s = document.createElement("script");
      s.src = "https://js.live.net/v7.2/OneDrive.js";
      s.async = true;
      s.crossOrigin = "anonymous";
      s.onload = () => console.log("OneDrive.js loaded");
      s.onerror = (e) => console.error("Failed to load OneDrive.js", e);
      document.body.appendChild(s);
    }
  }, []);

  // Old redirect-based flow handler (fine to keep)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("onedrive_result");
      if (raw) {
        sessionStorage.removeItem("onedrive_result");
        const res = JSON.parse(raw);
        const item = res?.value?.[0];
        if (item) {
          const folder: PickedFolder = {
            id: item.id,
            driveId:
              item.parentReference?.driveId ??
              item.parentReference?.id ??
              "",
            webUrl: item.webUrl,
            name: item.name,
          };
          console.log("📂 Restored picked folder from redirect:", folder);
          onPicked(folder);
        }
      }

      const err = sessionStorage.getItem("onedrive_error");
      if (err) {
        sessionStorage.removeItem("onedrive_error");
        console.error(
          "OneDrive picker error (from redirect):",
          JSON.parse(err)
        );
      }

      const cancelled = sessionStorage.getItem("onedrive_cancelled");
      if (cancelled) {
        sessionStorage.removeItem("onedrive_cancelled");
        console.log("OneDrive picker was cancelled.");
      }
    } catch (e) {
      console.error("Failed to process OneDrive redirect result:", e);
    }
  }, [onPicked]);

  // Guard so we don't double-open the picker on spam clicks
  const isOpeningRef = useRef(false);

  const openPicker = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      console.log("👉 Opening OneDrive folder picker…");
      if (isOpeningRef.current) {
        console.log(
          "OneDrive picker already opening/open – ignoring extra click."
        );
        return;
      }

      try {
        const OneDrive = (window as any).OneDrive;
        if (!OneDrive) {
          console.error("OneDrive.js not loaded yet");
          return;
        }

        isOpeningRef.current = true;

        // Our dedicated redirect helper page
        const redirectUri = `${window.location.origin}/onedrive-redirect.html`;

        // ❗ We do NOT pass accessToken here. We let the picker handle OAuth itself,
        // but give it an endpointHint pointing at our tenant’s OneDrive for Business.
        const odOptions: any = {
          clientId: msalInstance.getConfiguration().auth.clientId,
          action: "query", // we're selecting folders, not downloading files
          viewType: "folders",
          multiSelect: false,
          openInNewWindow: true,
          advanced: {
            redirectUri,
            endpointHint: OD_BUSINESS_ENDPOINT_HINT,
            isConsumerAccount: false,
          },
          success: (res: any) => {
            isOpeningRef.current = false;
            console.log("OneDrive picker success:", res);
            const item = res?.value?.[0];
            if (!item) {
              console.warn("OneDrive picker returned no items.");
              return;
            }

            const folder: PickedFolder = {
              id: item.id,
              driveId:
                item.parentReference?.driveId ??
                item.parentReference?.id ??
                "",
              webUrl: item.webUrl,
              name: item.name,
            };

            console.log("📂 Picked folder:", folder);
            onPicked(folder);
          },
          cancel: () => {
            isOpeningRef.current = false;
            console.log("OneDrive picker cancelled.");
          },
          error: (err: any) => {
            isOpeningRef.current = false;

            if (err?.errorCode === "popupOpen") {
              console.log(
                "OneDrive picker reported popupOpen (another picker is already open or popup blocked).",
                err
              );
            } else {
              console.error("OneDrive picker error:", err);
            }
          },
        };

        // Persist options/route for possible redirect-mode flows
        try {
          const returnRoute = window.location.pathname + window.location.search;
          console.log("Storing OneDrive picker return route:", returnRoute);

          sessionStorage.setItem("__returnToRoute", returnRoute);
          localStorage.setItem("__returnToRoute", returnRoute);

          const baseOptions = {
            clientId: odOptions.clientId,
            action: odOptions.action,
            viewType: odOptions.viewType,
            multiSelect: odOptions.multiSelect,
            openInNewWindow: odOptions.openInNewWindow,
            advanced: odOptions.advanced,
          };
          sessionStorage.setItem(
            "onedrive_options",
            JSON.stringify(baseOptions)
          );
          localStorage.setItem("onedrive_options", JSON.stringify(baseOptions));
        } catch (e) {
          console.warn(
            "Failed to write OneDrive redirect options to storage",
            e
          );
        }

        OneDrive.open(odOptions);
      } catch (err) {
        isOpeningRef.current = false;
        console.error("openPicker failed", err);
      }
    },
    [onPicked]
  );

  return (
    <button
      type="button"
      onClick={openPicker}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #ddd",
        cursor: "pointer",
        background: "#fff",
      }}
    >
      {label}
    </button>
  );
}
